const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => value == null || typeof value === 'string';
const ref = value => typeof value === 'string' && value.length > 0 || record(value) && typeof value._id === 'string' && value._id.length > 0;
const integer = value => Number.isInteger(value) && value >= 0;
// Lesson numbers are display labels (for example 2.2 or "2.10"), not indices.
const lessonNumber = value => typeof value === 'number' && Number.isFinite(value) && value >= 1
  || typeof value === 'string' && value.trim().length > 0;
const optionalArray = (value, item = () => true) => value === undefined || Array.isArray(value) && value.every(item);
const fields = (value, keys) => keys.every(key => text(value[key]));
export const isCurriculumPointer = value => record(value)
  && (value.kind === undefined || ['none', 'flatLessons', 'setsWithLessons'].includes(value.kind))
  && ['setIndex', 'lessonIndex', 'lessonInSetIndex'].every(key => value[key] == null || integer(value[key]))
  && (value.lessonNumber == null || lessonNumber(value.lessonNumber))
  && fields(value, ['setCode', 'setTitle', 'lessonTitle']);
export const isCurriculumProgress = value => isCurriculumPointer(value)
  && (value.progressVersion === undefined || integer(value.progressVersion))
  && (value.lastSession == null || ref(value.lastSession))
  && (value.lastAdvancedAt == null || typeof value.lastAdvancedAt === 'string' && Number.isFinite(Date.parse(value.lastAdvancedAt)));
const isProgramRef = value => typeof value === 'string' && value.length > 0 || record(value)
  && (ref(value) || ['id', 'shareSlug'].some(key => typeof value[key] === 'string' && value[key]))
  && fields(value, ['id', 'shareSlug']);
const programKinds = ['prayer', 'reading', 'music', 'text', 'quote', 'note'];
const isProgramItem = value => record(value) && programKinds.includes(value.kind)
  && fields(value, ['title', 'body', 'reference', 'mediaUrl']);
const isLesson = value => record(value)
  && (value.curriculumId == null || ref(value.curriculumId))
  && (value.pointer == null || isCurriculumPointer(value.pointer))
  && fields(value, ['requestedLesson', 'source', 'bookId', 'setId', 'lessonId', 'sourceSha256'])
  && (value.source == null || value.source === 'bahai-writings-data')
  && (value.sourceSha256 == null || /^[a-f0-9]{64}$/i.test(value.sourceSha256));
const isPlanBlock = value => record(value) && typeof value.id === 'string' && value.id.trim().length > 0
  && typeof value.title === 'string' && value.title.trim().length > 0
  && fields(value, ['type', 'body', 'sectionKey', 'sourceSectionTitle', 'sourceSectionKey', 'sourceLessonId'])
  && (value.minutes === undefined || typeof value.minutes === 'number' && Number.isFinite(value.minutes) && value.minutes >= 0 && value.minutes <= 480)
  && (value.sourceSectionIndex === undefined || integer(value.sourceSectionIndex));
const isChecklistItem = value => record(value) && typeof value.id === 'string' && value.id.trim().length > 0
  && typeof value.label === 'string' && value.label.trim().length > 0 && typeof value.completed === 'boolean';
const isPlan = value => record(value) && text(value.title)
  && optionalArray(value.blocks, isPlanBlock) && (value.blocks === undefined || value.blocks.length <= 50)
  && optionalArray(value.checklist, isChecklistItem) && (value.checklist === undefined || value.checklist.length <= 100);

// Absent details, omitted versions and partial public data are supported legacy
// projections. Generic/unknown type metadata and future versions remain opaque.
export function isTypedDetails(value, scope = 'session') {
  if (value == null) return true;
  if (!record(value) || !text(value.typeKey)
    || value.version !== undefined && (!Number.isInteger(value.version) || value.version <= 0)
    || value.data !== undefined && !record(value.data)) return false;
  const data = value.data;
  if (!data) return true;
  const maximum = value.typeKey === 'childrensClass' && scope === 'session' ? 2 : 1;
  if (value.version > maximum) return true;
  if (value.typeKey === 'devotional') {
    return scope === 'activity'
      ? fields(data, ['theme', 'audience', 'language'])
        && (data.defaultFormat == null || ['prayers-readings-music', 'open', 'guided', 'custom'].includes(data.defaultFormat))
      : optionalArray(data.program, isProgramItem) && fields(data, ['sessionTheme'])
        && optionalArray(data.speakers) && optionalArray(data.resources)
        && (data.programRef == null || isProgramRef(data.programRef));
  }
  if (value.typeKey === 'childrensClass') {
    return scope === 'activity'
      ? fields(data, ['grade', 'cohortLabel']) && (data.curriculumId == null || ref(data.curriculumId))
        && (data.curriculumMode == null || ['sequential', 'manual'].includes(data.curriculumMode))
      : (data.lesson == null || isLesson(data.lesson)) && (data.plan == null || isPlan(data.plan))
        && optionalArray(data.materials) && text(data.notes);
  }
  return true;
}
